import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../hooks/redux";
import { addGroup, updateGroup } from "../../store/slices/groupsSlice";
import { createTextValidation } from "../common/TextValidation";

interface GroupDialogProps {
  open: boolean;
  onClose: (refresh?: boolean) => void; // <-- Accepts optional boolean
  mode: "add" | "edit" | "view";
  group?: any;
}

const schema = yup.object().shape({
  name: createTextValidation({
    title: "Group name",
    minLength: 3,
    maxLength: 100,
  }),
});

const GroupDialog: React.FC<GroupDialogProps> = ({
  open,
  onClose,
  mode,
  group,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
    },
    mode: "onChange", // Enable real-time validation
  });

  useEffect(() => {
    if (open) {
      if (mode === "edit" && group) {
        reset({
          name: group.name || "",
        });
      } else {
        reset({
          name: "",
        });
      }
    }
  }, [open, mode, group, reset]);

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const requestData = {
        name: data.name,
        status: 1, // Always active
        isgst: 0, // Default GST configuration
        isprice: 0, // Default isprice value
        noofusers: 0, // Always 0
        createdby: localStorage.getItem("userId"),
      };

      if (mode === "add") {
        await dispatch(addGroup(requestData)).unwrap();
        toast.success("Group added successfully!");
        onClose(true); // Pass true to refresh
      } else {
        await dispatch(
          updateGroup({ id: group.id, data: requestData })
        ).unwrap();
        toast.success("Group updated successfully!");
        onClose(true); // Pass true to refresh
      }
    } catch (error) {
      toast.error("Failed to save group.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose(false); // Pass false, don't refresh on cancel
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {mode === "add"
            ? "Add New Group"
            : mode === "edit"
            ? "Edit Group"
            : "View Group"}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ position: "relative" }}>
          {/* Loader overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(255,255,255,0.7)",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              opacity: loading ? 0.5 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {/* Group Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Group Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  inputProps={{
                    maxLength: 30,
                  }}
                  disabled={mode === "view"}
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ minWidth: 100 }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GroupDialog;
